import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { newIdemKey } from "@/lib/idem";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MessageCircle, X, Send, Download, Share2, Loader2, AlertTriangle } from "lucide-react";
import CustomerEmailGate from "@/components/documents/CustomerEmailGate";
import WhatsAppCustomerGate from "@/components/documents/WhatsAppCustomerGate";
import { waLink } from "@/lib/wa";

// B-30 — reusable sharing for an ALREADY finalized document (sale / refund /
// payment receipt, statement). Never re-runs any financial/stock action —
// only shares a PDF that already exists. `onEmail(idempotencyKey)` posts to
// the document's own `/receipt/email` (durable per-click dedup via a fresh
// key every Send click) — a genuine later resend simply uses a new key.
// Customer Email Accuracy & Protection: the recipient is NEVER free-text here —
// it is always the saved customer's email (see CustomerEmailGate); the backend
// enforces this identically and ignores any client-supplied address.
// Four distinct, clearly labeled actions (Owner direction, 2026-10-25):
//  - Email Customer: the branded HTML email + official PDF attachment to the saved email.
//  - WhatsApp Customer: free Click to Chat to the saved WhatsApp number with a prefilled
//    text message only — never an arbitrary typed number, never a claim a PDF is attached.
//  - Share Document: hands the real PDF to the device/browser's own native share sheet so
//    the cashier can pick WhatsApp, Outlook, Drive, etc. themselves with the file attached.
//  - Download PDF: a plain, always-available file download, independent of the above.
// `onWhatsAppShared(method)` / native-share success are best-effort audit logs only, never
// a delivery claim — opening WhatsApp or a share sheet is not the same as the customer
// actually receiving anything.
export default function DocumentShareDialog({
  open, onOpenChange, title = "Share Document", docLabel = "", customerId = null,
  customerEmail = "", customerPhone = "", waMessage = "", onCustomerUpdated,
  getPdfBlob, onEmail, onWhatsAppShared, fileName = "document.pdf",
}) {
  const [custEmail, setCustEmail] = useState("");
  const [waNumber, setWaNumber] = useState("");
  const [sending, setSending] = useState(false);
  const [busyAction, setBusyAction] = useState(null); // "share" | "download" | null — separate so the spinner only shows on the button actually clicked
  const pdfBlobRef = useRef(null);
  const safeFileName = String(fileName || "document.pdf").replace(/[\\/]/g, "-"); // strip path separators — some Android share targets reject/mangle a File.name containing "/"

  useEffect(() => {
    if (open) { setCustEmail(customerEmail || ""); setWaNumber(customerPhone || ""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerEmail, customerPhone]);

  // Pre-fetch, same reasoning as QuotePreviewDialog: iOS Safari needs navigator.share
  // to run inside the click's user-activation window, so the blob must already be resolved.
  useEffect(() => {
    pdfBlobRef.current = null;
    if (open && getPdfBlob) getPdfBlob().then((b) => { pdfBlobRef.current = b; }).catch(() => { pdfBlobRef.current = null; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sendingRef = useRef(false); // synchronous guard: `sending` state alone lets two same-tick clicks both fire
  const doSendEmail = async () => {
    if (sendingRef.current || !custEmail) return;
    sendingRef.current = true;
    setSending(true);
    try {
      await onEmail(newIdemKey());
      toast.success(`Emailed to ${custEmail}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(apiError(detail) || "Could not send the email.");
    } finally { sendingRef.current = false; setSending(false); }
  };

  // Click to Chat — opens WhatsApp with the message already typed in; the cashier still has
  // to review it and tap Send inside WhatsApp themselves. Never assumes that counts as sent.
  const openWhatsAppCustomer = () => {
    const link = waLink(waNumber, waMessage);
    if (!link) { toast.error("Saved number looks invalid — use Edit Customer to fix it."); return; }
    window.open(link, "_blank", "noopener");
    onWhatsAppShared?.("click_to_chat");
  };

  const fetchPdf = async () => pdfBlobRef.current || await getPdfBlob();

  // Share Document — hands the real PDF to the OS's own share sheet so the cashier can pick
  // WhatsApp, Outlook, Drive, etc. themselves with the file genuinely attached. When the
  // browser/device doesn't support file sharing at all, says so plainly rather than silently
  // substituting a download under this button's name.
  const shareDocument = async () => {
    if (busyAction) return;
    setBusyAction("share");
    let blob;
    try { blob = await fetchPdf(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not prepare the PDF."); setBusyAction(null); return; }
    const file = new File([blob], safeFileName, { type: "application/pdf" });
    const canNativeShare = typeof navigator !== "undefined" && !!navigator.share && !!navigator.canShare
      && navigator.canShare({ files: [file] });
    if (!canNativeShare) {
      toast.message("Sharing isn't supported in this browser", { description: "Use Download PDF instead." });
      setBusyAction(null);
      return;
    }
    try {
      await navigator.share({ files: [file], title: docLabel });
      toast.success("Shared via your device.");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not share the file — try Download PDF instead.");
    } finally { setBusyAction(null); }
  };

  // Download PDF — a plain, always-available file download. Deliberately never attempts
  // native share; Share Document above is the only path that hands the file to another app.
  const downloadPdf = async () => {
    if (busyAction) return;
    setBusyAction("download");
    try {
      const blob = await fetchPdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = safeFileName; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not prepare the PDF.");
    } finally { setBusyAction(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="document-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">{title}</DialogTitle>
          <DialogDescription className="sr-only">Email, WhatsApp or share {docLabel} with the customer. This never changes the record.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!customerId && (
            <div className="rounded-lg border border-dashed border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="no-customer-linked-notice">
              <p className="flex items-center gap-1.5 font-medium text-amber-700 dark:text-amber-400"><AlertTriangle className="w-4 h-4" /> No customer linked</p>
              <p className="text-xs text-muted-foreground mt-1">This document has no customer account linked, so there's no saved address or number to send it to. Select or create a customer first — Share Document and Download PDF are still available.</p>
            </div>
          )}
          {customerId && (
            <CustomerEmailGate customerId={customerId} email={custEmail} onEmailResolved={setCustEmail} onCustomerUpdated={onCustomerUpdated}>
              <div>
                <label className="text-xs text-muted-foreground">Email Customer</label>
                <div className="flex gap-2 mt-1 items-center">
                  <div className="flex-1 h-10 px-3 rounded-md border border-border bg-secondary/40 flex items-center text-sm truncate" data-testid="doc-share-email-readonly">{custEmail}</div>
                  <Button disabled={sending} onClick={doSendEmail} className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0" data-testid="doc-share-email-btn">
                    <Send className="w-4 h-4 mr-1.5" /> {sending ? "Sending…" : "Send"}
                  </Button>
                </div>
              </div>
            </CustomerEmailGate>
          )}
          {customerId && (
            <WhatsAppCustomerGate customerId={customerId} number={waNumber} onNumberResolved={setWaNumber} onCustomerUpdated={onCustomerUpdated}>
              <div>
                <label className="text-xs text-muted-foreground">WhatsApp Customer</label>
                <div className="flex gap-2 mt-1 items-center">
                  <div className="flex-1 h-10 px-3 rounded-md border border-border bg-secondary/40 flex items-center text-sm truncate" data-testid="doc-share-whatsapp-readonly">{waNumber}</div>
                  <Button variant="outline" className="text-emerald-600 border-emerald-500/40 shrink-0" onClick={openWhatsAppCustomer} data-testid="doc-share-whatsapp-btn">
                    <MessageCircle className="w-4 h-4 mr-1.5" /> Open WhatsApp
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Opens WhatsApp with the message ready — review it and tap Send yourself.</p>
              </div>
            </WhatsAppCustomerGate>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" disabled={!!busyAction} onClick={shareDocument} data-testid="doc-share-document-btn">
              {busyAction === "share" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Share2 className="w-4 h-4 mr-1.5" />} Share Document
            </Button>
            <Button variant="outline" disabled={!!busyAction} onClick={downloadPdf} data-testid="doc-share-pdf-btn">
              {busyAction === "download" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} Download PDF
            </Button>
          </div>
          <Button variant="ghost" className="w-full" onClick={() => onOpenChange(false)} data-testid="doc-share-close-btn">
            <X className="w-4 h-4 mr-1.5" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
